'use client';

import { useCallback, useRef } from 'react';

interface CachedToken {
  token: string;
  /** Absolute unix ms at which the token should be considered stale. */
  expiresAt: number;
}

const SKEW_MS = 30_000;

/**
 * Returns a stable async `getToken()` that hits `/api/callcenter/dev-token`
 * and caches the result until near-expiry. Safe to call concurrently —
 * overlapping requests share the same in-flight promise.
 */
export function useAuthenticatedToken(): () => Promise<string> {
  const tokenRef = useRef<CachedToken | null>(null);
  const inflightRef = useRef<Promise<string> | null>(null);

  return useCallback(async () => {
    const now = Date.now();
    const cached = tokenRef.current;
    if (cached && cached.expiresAt - SKEW_MS > now) {
      return cached.token;
    }
    if (inflightRef.current) return inflightRef.current;

    const promise = (async () => {
      const res = await fetch('/api/callcenter/dev-token', { cache: 'no-store' });
      if (!res.ok) {
        throw new Error(`dev-token mint failed: ${res.status}`);
      }
      const data = (await res.json()) as { token: string; expiresIn: number };
      tokenRef.current = {
        token: data.token,
        expiresAt: Date.now() + (data.expiresIn ?? 3600) * 1000,
      };
      inflightRef.current = null;
      return data.token;
    })();
    inflightRef.current = promise;
    return promise;
  }, []);
}
